import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Paper, Box, Typography, TextField, Chip, IconButton,
  Tooltip, Button, Skeleton, Grid, Select, MenuItem, FormControl,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  SearchOff as SearchOffIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import Pagination from '@mui/material/Pagination';
import SimpleAutoCompleteMultiple from '../../../../components/auto-complete/autocomplete-multiple';
import SimpleCheckBox from '../../../../components/checkbox/check';
import SearchBarDialog from '../search-bar-dialog';

function SkeletonCard() {
  return (
    <Paper elevation={1} sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
      <Skeleton variant="circular" width={28} height={28} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="55%" height={22} />
        <Skeleton variant="text" width="35%" height={18} />
      </Box>
      <Skeleton variant="rectangular" width={64} height={24} sx={{ borderRadius: 4 }} />
      <Skeleton variant="circular" width={32} height={32} />
      <Skeleton variant="circular" width={32} height={32} />
      <Skeleton variant="circular" width={32} height={32} />
    </Paper>
  );
}

function FilterCard({ item, onApply, onEditStep, onDeleteStep, admGlobal, intl }) {
  return (
    <Paper elevation={1} sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
      <IconButton size="small" disableRipple sx={{ color: item.isfavorite ? '#ffb400' : '#bdbdbd', cursor: 'default' }}>
        {item.isfavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
      </IconButton>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={600} noWrap>
          {item.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {item.exhibitionName}
          {item.filterCreatorName ? ` · ${intl.formatMessage({ id: 'created.by' })}: ${item.filterCreatorName}` : ''}
        </Typography>
      </Box>

      <Chip
        label={item.isprivate
          ? intl.formatMessage({ id: 'private' })
          : intl.formatMessage({ id: 'filter.public' })}
        size="small"
        variant={item.isprivate ? 'outlined' : 'filled'}
        color={item.isprivate ? 'default' : 'primary'}
        sx={{ minWidth: 72 }}
      />

      <Tooltip title={intl.formatMessage({ id: 'apply' })}>
        <IconButton color="primary" size="small" onClick={() => onApply(item.data)}>
          <PlayArrowIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {(admGlobal || item.deleteId.user) && (
        <Tooltip title={intl.formatMessage({ id: 'edit' })}>
          <IconButton color="secondary" size="small" onClick={() => onEditStep(item.editId.id)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {(admGlobal || item.deleteId.user) && (
        <Tooltip title={intl.formatMessage({ id: 'delete' })}>
          <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => onDeleteStep(item.deleteId.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
}

function EmptyState({ intl }) {
  return (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <SearchOffIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
      <Typography color="text.secondary">
        {intl.formatMessage({ id: 'rows.show' })}
      </Typography>
    </Box>
  );
}

const ROWS_PER_PAGE = 10;

function Step1({ loading, filters, searchValue, onSearchChange, onSearch, onClearSearch, onApply, onEditStep, onDeleteStep, admGlobal, intl, classes }) {
  const [page, setPage] = React.useState(1);
  const [sortField, setSortField] = React.useState('');

  React.useEffect(() => { setPage(1); }, [filters]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') searchValue ? onSearch() : onClearSearch();
  };

  const handleSearch = () => {
    searchValue ? onSearch() : onClearSearch();
  };

  const sortedFilters = (() => {
    if (!sortField) return filters;
    return [...filters].sort((a, b) => {
      if (sortField === 'name_asc') return a.name.localeCompare(b.name);
      if (sortField === 'name_desc') return b.name.localeCompare(a.name);
      if (sortField === 'isfavorite') return (b.isfavorite ? 1 : 0) - (a.isfavorite ? 1 : 0);
      if (sortField === 'ispublic') return (a.isprivate ? 1 : 0) - (b.isprivate ? 1 : 0);
      return 0;
    });
  })();

  const totalPages = Math.ceil(sortedFilters.length / ROWS_PER_PAGE);
  const pageStart = sortedFilters.length === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const pageEnd = Math.min(page * ROWS_PER_PAGE, sortedFilters.length);
  const paginatedFilters = sortedFilters.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  return (
    <Box>
      <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: 2, p: 1, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 200 }}>
            <TextField
              fullWidth
              placeholder={intl.formatMessage({ id: 'search' })}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 4px 0 rgba(0,0,0,0.07)',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: '0 2px 8px 0 rgba(0,0,0,0.13)' },
                  '&.Mui-focused': { boxShadow: '0 2px 10px 0 rgba(0,0,0,0.15)' },
                },
              }}
            />
            <Tooltip title={intl.formatMessage({ id: 'clear' })}>
              <IconButton
                onClick={onClearSearch}
                sx={{ backgroundColor: '#e0e0e0', borderRadius: 1.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: 'search' })}>
              <IconButton
                aria-label="search"
                onClick={handleSearch}
                sx={{ backgroundColor: 'primary.main', borderRadius: 1.5 }}
              >
                <SearchIcon fontSize="small" className={classes.icon} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
              {intl.formatMessage({ id: 'order' })}:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 180, backgroundColor: '#fff', borderRadius: 1 }}>
              <Select
                value={sortField}
                onChange={(e) => { setSortField(e.target.value); setPage(1); }}
                displayEmpty
              >
                <MenuItem value="">{intl.formatMessage({ id: 'default' })}</MenuItem>
                <MenuItem value="name_asc">{intl.formatMessage({ id: 'name' })} (A-Z)</MenuItem>
                <MenuItem value="name_desc">{intl.formatMessage({ id: 'name' })} (Z-A)</MenuItem>
                <MenuItem value="isfavorite">{intl.formatMessage({ id: 'save.as.favorite' })}</MenuItem>
                <MenuItem value="ispublic">{intl.formatMessage({ id: 'filter.public' })}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} md={6} key={i}>
              <SkeletonCard />
            </Grid>
          ))}
        </Grid>
      ) : filters.length === 0 ? (
        <EmptyState intl={intl} />
      ) : (
        <>
          <Grid container spacing={2}>
            {paginatedFilters.map((item) => (
              <Grid item xs={12} md={6} key={item.id}>
                <FilterCard
                  item={item}
                  onApply={onApply}
                  onEditStep={onEditStep}
                  onDeleteStep={onDeleteStep}
                  admGlobal={admGlobal}
                  intl={intl}
                />
              </Grid>
            ))}
          </Grid>
          <Grid container sx={{ mt: 1.5, alignItems: 'center' }}>
            <Grid item xs={12} md={6}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                showFirstButton
                showLastButton
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: { xs: 1, md: 0 } }}>
              <Typography variant="body2" color="text.secondary">
                {pageStart} {intl.formatMessage({ id: 'to' })} {pageEnd} {intl.formatMessage({ id: 'of' })} {filters.length} {intl.formatMessage({ id: 'register' })}
              </Typography>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

function Step2({
  editData, contentFilterEdit, contentOrderEdit,
  organizationList, filterOptions, filterSelectedEdit, searchValueEdit,
  onInputChange, onChangeOrgs, onChangeEditField,
  onDeleteFilterChip, onDeleteOrderChip,
  onAddFilterEdit, onClearFieldsEdit, onCallSearchEdit, onHandleChangeFieldValue,
  admGlobal, isAdmOrg, isCoordenator, isClient, isTechnical, isAdm,
  currentUserId, orderFields, classes, intl,
}) {
  const requiresOrg = !admGlobal && (isAdmOrg || isCoordenator) && !editData.isPrivate;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          inputProps={{ maxLength: 255 }}
          fullWidth
          label={intl.formatMessage({ id: 'name' })}
          variant="outlined"
          name="name"
          onChange={(e) => onInputChange(e, 'editTableCustomDialog')}
          value={editData ? editData.name : ''}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          inputProps={{ maxLength: 15 }}
          fullWidth
          label={intl.formatMessage({ id: 'exhibition.name' })}
          variant="outlined"
          name="exhibitionName"
          onChange={(e) => onInputChange(e, 'editTableCustomDialog')}
          value={editData ? editData.exhibitionName : ''}
          required
        />
      </Grid>

      <Grid item xs={12}>
        <SimpleAutoCompleteMultiple
          label={`${intl.formatMessage({ id: 'organization' })}${requiresOrg ? '*' : ''}`}
          options={organizationList}
          stateName="tableCustomFilterOrganizationIds"
          name="tableCustomFilterOrganizationIds"
          changeSelect={onChangeOrgs}
          selected={editData ? editData.tableCustomFilterOrganizationIds : []}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '& > div': { display: 'flex', alignItems: 'center' } }}>
          <SimpleCheckBox
            label={intl.formatMessage({ id: 'private' })}
            name="isPrivate"
            stateName="isPrivate"
            changeSelect={onChangeEditField}
            selected={editData ? editData.isPrivate : false}
            disabled={(isClient) || (isTechnical && !isAdm && !admGlobal)}
          />
          <Tooltip title={intl.formatMessage({ id: 'called.list.tooltip.turns.custom.filter.private' })}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '& > div': { display: 'flex', alignItems: 'center' } }}>
          <SimpleCheckBox
            label={intl.formatMessage({ id: 'save.as.favorite' })}
            name="isFavorite"
            stateName="isFavorite"
            changeSelect={onChangeEditField}
            selected={editData ? editData.isFavorite : false}
          />
          <Tooltip title={intl.formatMessage({ id: 'called.list.tooltip.saves.filter.as.favorite' })}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '& > div': { display: 'flex', alignItems: 'center' } }}>
          <SimpleCheckBox
            label={intl.formatMessage({ id: 'standard.filter' })}
            name="isDefault"
            stateName="isDefault"
            changeSelect={onChangeEditField}
            selected={editData ? editData.isDefault : false}
          />
          <Tooltip title={intl.formatMessage({ id: 'called.list.tooltip.saves.filter.as.standard' })}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '& > div': { display: 'flex', alignItems: 'center' } }}>
          <SimpleCheckBox
            label={intl.formatMessage({ id: 'display.filter.in.called.list' })}
            name="displayFilterInCalledList"
            stateName="displayFilterInCalledList"
            changeSelect={onChangeEditField}
            selected={editData ? editData.displayFilterInCalledList : false}
          />
          <Tooltip title={intl.formatMessage({ id: 'display.filter.in.called.list.tooltip' })}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
      </Grid>

      {admGlobal && editData && editData.userId != currentUserId && (
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '& > div': { display: 'flex', alignItems: 'center' } }}>
            <SimpleCheckBox
              label={intl.formatMessage({ id: 'display.filter.in.creator.called.list' })}
              name="displayFilterInCreatorCalledList"
              stateName="displayFilterInCreatorCalledList"
              changeSelect={onChangeEditField}
              selected={editData ? editData.displayFilterInCreatorCalledList : false}
            />
            <Tooltip title={intl.formatMessage({ id: 'display.filter.in.creator.called.list.tooltip' })}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
        </Grid>
      )}

      <Grid item xs={12}>
        <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: 2, p: 2 }}>
          {(contentFilterEdit || contentOrderEdit) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {(contentFilterEdit ?? '').split('\n').map((line, key) =>
                line.trim() ? (
                  <Chip
                    key={`f-${key}`}
                    label={line}
                    onDelete={() => onDeleteFilterChip(key)}
                    color="primary"
                    variant="outlined"
                  />
                ) : null
              )}
              {(contentOrderEdit ?? '').split('\n').map((line, key) =>
                line.trim() ? (
                  <Chip
                    key={`o-${key}`}
                    label={line}
                    onDelete={() => onDeleteOrderChip(key)}
                    color="secondary"
                    variant="outlined"
                  />
                ) : null
              )}
            </Box>
          )}
          <SearchBarDialog
            field={filterSelectedEdit}
            filterValue={searchValueEdit}
            filterName="searchValueEdit"
            filters={filterOptions}
            orderFields={orderFields}
            addFilterEdit={onAddFilterEdit}
            clearFieldsEdit={onClearFieldsEdit}
            callSearch={onCallSearchEdit}
            handleChangeFieldValue={onHandleChangeFieldValue}
            classes={classes}
            intl={intl}
          />
        </Box>
      </Grid>
    </Grid>
  );
}

function SavedFiltersModal(props) {
  const {
    open, step, loading, onClose,
    filters, searchValue, onSearchChange, onSearch, onClearSearch,
    onApply, onEditStep, onDeleteStep,
    editData, contentFilterEdit, contentOrderEdit,
    organizationList, filterOptions, filterSelectedEdit, searchValueEdit,
    onBack, onSave, onInputChange, onChangeOrgs, onChangeEditField,
    onDeleteFilterChip, onDeleteOrderChip,
    onAddFilterEdit, onClearFieldsEdit, onCallSearchEdit, onHandleChangeFieldValue,
    admGlobal, isAdmOrg, isCoordenator, isClient, isTechnical, isAdm,
    currentUserId, orderFields, intl, classes,
  } = props;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="saved-filters-dialog-title"
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle id="saved-filters-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        {step === 1 ? (
          <>
            <span>{intl.formatMessage({ id: 'custom.filters' })}</span>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={onBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <span>{intl.formatMessage({ id: 'custom.filter.edit' })}</span>
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <div style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 140px)', padding: 24 }}>
          {step === 1
            ? <Step1 {...{ loading, filters, searchValue, onSearchChange, onSearch, onClearSearch, onApply, onEditStep, onDeleteStep, admGlobal, intl, classes }} />
            : <Step2 {...{ editData, contentFilterEdit, contentOrderEdit, organizationList, filterOptions, filterSelectedEdit, searchValueEdit, onInputChange, onChangeOrgs, onChangeEditField, onDeleteFilterChip, onDeleteOrderChip, onAddFilterEdit, onClearFieldsEdit, onCallSearchEdit, onHandleChangeFieldValue, admGlobal, isAdmOrg, isCoordenator, isClient, isTechnical, isAdm, currentUserId, orderFields, classes, intl }} />
          }
        </div>
      </DialogContent>

      {step === 2 && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={onBack}>
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          <Button variant="contained" color="primary" onClick={onSave}>
            {intl.formatMessage({ id: 'save' })}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

SavedFiltersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  step: PropTypes.oneOf([1, 2]).isRequired,
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onClearSearch: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onEditStep: PropTypes.func.isRequired,
  onDeleteStep: PropTypes.func.isRequired,
  editData: PropTypes.object.isRequired,
  contentFilterEdit: PropTypes.string.isRequired,
  contentOrderEdit: PropTypes.string.isRequired,
  organizationList: PropTypes.array.isRequired,
  filterOptions: PropTypes.array.isRequired,
  filterSelectedEdit: PropTypes.any,
  searchValueEdit: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onChangeOrgs: PropTypes.func.isRequired,
  onChangeEditField: PropTypes.func.isRequired,
  onDeleteFilterChip: PropTypes.func.isRequired,
  onDeleteOrderChip: PropTypes.func.isRequired,
  onAddFilterEdit: PropTypes.func.isRequired,
  onClearFieldsEdit: PropTypes.func.isRequired,
  onCallSearchEdit: PropTypes.func.isRequired,
  onHandleChangeFieldValue: PropTypes.func.isRequired,
  admGlobal: PropTypes.bool,
  isAdmOrg: PropTypes.bool,
  isCoordenator: PropTypes.bool,
  isClient: PropTypes.bool,
  isTechnical: PropTypes.bool,
  isAdm: PropTypes.bool,
  currentUserId: PropTypes.number,
  orderFields: PropTypes.array,
  intl: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

export default SavedFiltersModal;
